<template>
    <teleport v-if="renderModal && hasHeader" :to="`#modal-header-root-${ modalGuid }`">
        <slot name="header" />
    </teleport>
    <teleport v-if="renderModal" :to="`#modal-root-${ modalGuid }`">
        <slot />
    </teleport>
    <teleport v-if="renderModal && hasFooter" :to="`#modal-footer-root-${ modalGuid }`">
        <slot name="footer" />
    </teleport>
</template>

<script lang="ts" setup>
import {
    computed, nextTick, ref, useSlots, watch,
} from 'vue';
import { useCentralModalStore } from '@/stores/centralModalStore.ts';
import { IModalConfig, ModalSize } from '@/interfaces/general/IModalConfig.ts';

const props = defineProps<{
    header: string;
    visible?: boolean;
    size?: ModalSize;
    hideHeader?: boolean;
}>();
const centralModalStore = useCentralModalStore();
const slots = useSlots();
const emit = defineEmits<{
    'update:visible': [boolean];
    'hide': [void];
}>();

const hasHeader = computed(() => !!slots.header);
const hasFooter = computed(() => !!slots.footer);
const modalGuid = crypto.randomUUID();
const renderModal = ref<boolean>(false);

const config = computed<IModalConfig>(() => ({
    label: props.header,
    guid: modalGuid,
    hasFooter: hasFooter.value,
    size: props.size,
    hideHeader: props.hideHeader ?? false,
}));

watch(() => props.visible, async (newVisibility) => {
    if (!newVisibility) {
        const top = centralModalStore.currentVisibleModal;
        if (top?.guid === modalGuid && !centralModalStore.shouldAllowClosingTopModal()) {
            emit('update:visible', true);
            return;
        }
        centralModalStore.closeModalByGuid(modalGuid);
        return;
    }
    centralModalStore.addModalToStack(config.value);
    await nextTick();
    renderModal.value = true;
}, { immediate: true });

watch(() => centralModalStore.modalStack.some(x => x.guid === modalGuid), (isInStack, wasInStack) => {
    if (wasInStack && !isInStack) {
        emit('update:visible', false);
        emit('hide');
        renderModal.value = false;
    }
});
</script>
